import { FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Copy,
  Loader2,
  Plus,
  Trash2,
  CheckCircle2,
  AlertTriangle
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { createKey, listKeys, revokeKey } from '@/api/keys';

export function KeysScreen() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['keys'],
    queryFn: listKeys
  });

  const [showCreate, setShowCreate] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [rawKey, setRawKey] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: ({ name }: { name: string }) => createKey(name),
    onSuccess: r => {
      setRawKey(r.apiKey);
      setShowCreate(false);
      setNewKeyName('');
      qc.invalidateQueries({ queryKey: ['keys'] });
    },
    onError: (e: Error) => toast.error(e.message || 'Could not create key')
  });

  const revokeMut = useMutation({
    mutationFn: (id: string) => revokeKey(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['keys'] });
      toast.success('Key revoked');
    },
    onError: (e: Error) => toast.error(e.message || 'Could not revoke')
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!newKeyName.trim()) return;
    createMut.mutate({ name: newKeyName.trim() });
  };

  return (
    <div className="space-y-8">
      <Toaster theme="dark" position="bottom-right" richColors />

      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-bold text-nurev-text">API Keys</h1>
          <p className="mt-1 text-sm text-nurev-muted">
            Bearer tokens prefixed with{' '}
            <code className="code-pill">nrev_</code>. Use them with{' '}
            <code className="code-pill">Authorization: Bearer …</code> against{' '}
            <code className="code-pill">https://api.app.nurev.io/v1</code>.
          </p>
        </div>
        <button
          className="btn-primary"
          onClick={() => {
            setShowCreate(true);
            setRawKey(null);
          }}
        >
          <Plus className="h-4 w-4" /> New key
        </button>
      </header>

      {rawKey && <NewKeyDisplay rawKey={rawKey} onDismiss={() => setRawKey(null)} />}

      {showCreate && (
        <form onSubmit={onSubmit} className="card-tight">
          <label className="label">Key name</label>
          <div className="flex gap-3">
            <input
              autoFocus
              required
              type="text"
              placeholder="e.g. production, staging, my-ingest-script"
              className="input flex-1"
              value={newKeyName}
              onChange={e => setNewKeyName(e.target.value)}
            />
            <button
              type="submit"
              disabled={createMut.isPending || !newKeyName.trim()}
              className="btn-primary"
            >
              {createMut.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Create'
              )}
            </button>
            <button
              type="button"
              className="btn-ghost"
              onClick={() => {
                setShowCreate(false);
                setNewKeyName('');
              }}
            >
              Cancel
            </button>
          </div>
          <p className="mt-2 text-xs text-nurev-muted">
            Name is for your own organization — pick something that tells you
            where this key is deployed.
          </p>
        </form>
      )}

      {isLoading ? (
        <div className="flex items-center gap-3 text-nurev-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : !data || data.length === 0 ? (
        <div className="card text-center text-sm text-nurev-muted">
          No API keys yet. Click <strong>New key</strong> to create your first.
        </div>
      ) : (
        <div className="card overflow-hidden p-0">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-nurev-border text-xs uppercase tracking-wide text-nurev-muted">
              <tr>
                <th className="px-5 py-3 font-semibold">Name</th>
                <th className="px-5 py-3 font-semibold">Prefix</th>
                <th className="px-5 py-3 font-semibold">Status</th>
                <th className="px-5 py-3 font-semibold">Last used</th>
                <th className="px-5 py-3 font-semibold">Created</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {data.map(k => (
                <tr
                  key={k._id}
                  className="border-b border-nurev-border last:border-0"
                >
                  <td className="px-5 py-3 text-nurev-text">{k.name}</td>
                  <td className="px-5 py-3">
                    <code className="code-pill">{k.keyPrefix}…</code>
                  </td>
                  <td className="px-5 py-3">
                    <StatusPill status={k.status} />
                  </td>
                  <td className="px-5 py-3 text-nurev-muted">
                    {k.lastUsedAt
                      ? new Date(k.lastUsedAt).toLocaleString()
                      : '—'}
                  </td>
                  <td className="px-5 py-3 text-nurev-muted">
                    {new Date(k.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-5 py-3 text-right">
                    {k.status === 'active' && (
                      <button
                        className="btn-ghost"
                        title="Revoke this key"
                        disabled={revokeMut.isPending}
                        onClick={() => {
                          if (
                            confirm(
                              `Revoke key "${k.name}"? This cannot be undone.`
                            )
                          ) {
                            revokeMut.mutate(k._id);
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function NewKeyDisplay({
  rawKey,
  onDismiss
}: {
  rawKey: string;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(rawKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="rounded-xl border border-amber-500/50 bg-amber-500/5 p-5">
      <div className="mb-3 flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-400" />
        <div>
          <h3 className="text-sm font-semibold text-nurev-text">
            Copy this key — it won't be shown again
          </h3>
          <p className="mt-1 text-xs text-nurev-muted">
            The full key is only revealed once. We store a hash and can
            never recover the raw value.
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <code className="flex-1 select-all rounded-lg border border-nurev-border bg-nurev-surface px-3 py-2 font-mono text-xs text-nurev-text">
          {rawKey}
        </code>
        <button onClick={copy} className="btn-secondary">
          {copied ? (
            <>
              <CheckCircle2 className="h-4 w-4 text-green-400" /> Copied
            </>
          ) : (
            <>
              <Copy className="h-4 w-4" /> Copy
            </>
          )}
        </button>
        <button onClick={onDismiss} className="btn-ghost">
          Dismiss
        </button>
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const base =
    'inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider';
  if (status === 'active')
    return (
      <span className={`${base} bg-green-500/15 text-green-300`}>active</span>
    );
  if (status === 'revoked')
    return (
      <span className={`${base} bg-red-500/15 text-red-300`}>revoked</span>
    );
  return (
    <span className={`${base} bg-nurev-surface-lift text-nurev-muted`}>
      {status}
    </span>
  );
}
